import React from "react";
import { useState, useEffect, useRef } from "react";
import { fetchAllWithPagination } from "@/utils/helper/graphql-helper";
import { generateClient } from "@/utils/helper/aws-generateClient-singleton";
import { listVariantAttributes } from "@/graphql/queries";
import { UseAttributesProps, VariantAttribute } from "@/types/AppTypes";

const useAttributes = ({
  filter = {},
  skip = false,
  client = null,
  dispatch,
  setAllVariantAttributes,
}: UseAttributesProps = {}) => {
  const [attributes, setAttributes] = useState<VariantAttribute[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const graphqlClient = client || generateClient();

  const fetchAttributes = async () => {
    if (skip) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const items = await fetchAllWithPagination({
        query: listVariantAttributes,
        client: graphqlClient,
        setLoader: setLoading,
        signal: abortControllerRef.current.signal,
        showToast: false,
      });

      if (!abortControllerRef.current.signal.aborted) {
        setAttributes(items);
        if (dispatch && setAllVariantAttributes) {
          dispatch(setAllVariantAttributes(items));
        }
      }
    } catch (err: any) {
      if (!abortControllerRef.current?.signal.aborted) {
        console.error("Error fetching variant attributes:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchAttributes();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [skip, JSON.stringify(filter)]);

  const refreshAttributes = () => {
    fetchAttributes();
  };

  return {
    attributes,
    loading,
    error,
    refreshAttributes,
  };
};

export default useAttributes;
